import { Button } from "@bandwith/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, type MusicianItem } from "../lib/api";
import { useIsAdmin, useSession } from "../lib/auth";
import { musiciansQuery } from "../lib/queries";
import { useSignInRedirect } from "../lib/useSignInRedirect";

export function AdminUsers() {
  const { session, isLoading: sessionLoading } = useSession();
  const signInRedirect = useSignInRedirect();
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const currentUserId = session?.user?.id ?? null;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const deleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    data: musicians = [],
    isLoading,
    error: fetchError,
  } = useQuery({
    ...musiciansQuery(),
    enabled: isAdmin,
  });

  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
    };
  }, []);

  const deleteMusicianMutation = useMutation({
    mutationFn: async (musicianId: string) => {
      const response = await api.musicians[":id"].$delete({
        param: { id: musicianId },
      });
      if (!response.ok) {
        throw new Error("Failed to delete user");
      }
      return musicianId;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(musiciansQuery().queryKey, (prev) =>
        prev?.filter((m) => m.id !== deletedId),
      );
      setConfirmingDeleteId(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to delete user");
      setConfirmingDeleteId(null);
    },
  });

  const handleDeleteClick = (musicianId: string) => {
    if (confirmingDeleteId === musicianId) {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
        deleteTimeoutRef.current = null;
      }
      deleteMusicianMutation.mutate(musicianId);
    } else {
      setConfirmingDeleteId(musicianId);
      deleteTimeoutRef.current = setTimeout(
        () => setConfirmingDeleteId(null),
        3000,
      );
    }
  };

  const updateMusicianMutation = useMutation({
    mutationFn: async ({
      musicianId,
      name,
    }: {
      musicianId: string;
      name: string;
    }) => {
      const response = await api.musicians[":id"].$patch({
        param: { id: musicianId },
        json: { name },
      });
      if (!response.ok) {
        throw new Error("Failed to update musician");
      }
      return { musicianId, name };
    },
    onSuccess: ({ musicianId, name }) => {
      queryClient.setQueryData(musiciansQuery().queryKey, (prev) =>
        prev?.map((m) => (m.id === musicianId ? { ...m, name } : m)),
      );
      setEditingId(null);
      setEditName("");
    },
    onError: (err) => {
      setError(
        err instanceof Error ? err.message : "Failed to update musician",
      );
    },
  });

  const isPending =
    updateMusicianMutation.isPending || deleteMusicianMutation.isPending;

  const startEditing = (musician: MusicianItem) => {
    setEditingId(musician.id);
    setEditName(musician.name ?? "");
    setError(null);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName("");
    setError(null);
  };

  const saveEdit = (musicianId: string) => {
    setError(null);
    updateMusicianMutation.mutate({ musicianId, name: editName });
  };

  const handleKeyDown = (e: React.KeyboardEvent, musicianId: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit(musicianId);
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  if (sessionLoading) {
    return <div className="text-text-muted">Loading...</div>;
  }

  if (!session?.user) {
    return (
      <div>
        <p className="mb-4 text-text-muted">
          You need to sign in to view this page.
        </p>
        <button
          type="button"
          onClick={() => signInRedirect("/admin/users")}
          className="text-amber hover:underline"
        >
          Sign In
        </button>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div>
        <p className="text-red-500">
          You don't have permission to view this page.
        </p>
        <Link to="/" className="text-amber hover:underline">
          Go home
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-text-muted">Loading users...</div>;
  }

  if (fetchError) {
    return <div className="text-red-500">Error: {fetchError.message}</div>;
  }

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">Users</h1>
      <div className="inline-block min-w-full rounded-lg border border-border bg-bg-secondary">
        {error && (
          <div className="border-b border-red-muted bg-red-muted/10 px-4 py-2 text-sm text-red-muted">
            {error}
          </div>
        )}
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-bg-tertiary text-left text-sm text-text-muted">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {musicians.map((user) => (
              <tr key={user.id} className="text-sm">
                <td className="px-4 py-3">
                  {editingId === user.id ? (
                    <div className="flex flex-col gap-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, user.id)}
                        disabled={isPending}
                        className="w-full rounded border border-amber bg-bg-tertiary px-2 py-1 text-sm text-text-primary focus:outline-none disabled:opacity-50"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveEdit(user.id)}
                          disabled={isPending}
                        >
                          {isPending ? "..." : "Save"}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={cancelEditing}
                          disabled={isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditing(user)}
                      className="group flex items-center gap-2 text-text-primary hover:text-amber"
                      title="Click to edit name"
                    >
                      <span>{user.name ?? "—"}</span>
                      <Pencil
                        className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100"
                        role="img"
                        aria-label="Edit"
                      />
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-text-muted">{user.email ?? "—"}</span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      user.role === "admin"
                        ? "bg-amber/20 text-amber"
                        : "bg-text-muted/20 text-text-muted"
                    }`}
                  >
                    {user.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      user.hasSignedIn
                        ? "bg-green-muted/20 text-green-muted"
                        : "bg-text-muted/20 text-text-muted"
                    }`}
                  >
                    {user.hasSignedIn ? "Signed In" : "Never"}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-text-muted">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  {user.id === currentUserId ? (
                    <span className="text-xs text-text-muted">You</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(user.id)}
                      disabled={isPending || editingId !== null}
                      className={`rounded p-1.5 transition-colors disabled:opacity-50 ${
                        confirmingDeleteId === user.id
                          ? "bg-red-muted text-on-accent hover:bg-red-600"
                          : "text-text-muted hover:bg-bg-tertiary hover:text-red-muted"
                      }`}
                      aria-label={
                        confirmingDeleteId === user.id
                          ? "Confirm delete"
                          : "Delete user"
                      }
                      title={
                        confirmingDeleteId === user.id
                          ? "Click again to confirm"
                          : "Delete user"
                      }
                    >
                      {confirmingDeleteId === user.id ? (
                        <Check className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {musicians.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-text-muted">
            No users found
          </p>
        )}
      </div>
    </div>
  );
}
